import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import {
  DatatableColumn,
  DatatableRow,
  DatatableRowActionButton,
} from '../../../shared/tables/datatable/datatable.models';
import { MisconfiguredVoiceAppsResource } from './misconfigured-voice-apps.resource';
import {
  EntityType,
  InvalidVoiceAppEntity,
  MisconfigurationType,
  MisconfiguredVoiceApp,
  MisconfiguredVoiceAppReport,
  ZpmPrivilege,
} from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { AuthenticationContext } from '../../../shared/contexts/authentication.context';
import { DatatableFilterService } from '../../../shared/tables/datatable/datatable-filter.service';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { TranslateService } from '@ngx-translate/core';

enum VoiceAppFilterType {
  AUTO_ATTENDANT = 'Auto Attendant',
  CALL_QUEUE = 'Call Queue',
}

interface MisconfiguredVoiceAppRow extends DatatableRow {
  guid: string;
  name: string;
  voiceAppType: string;
  misconfigurationType: string;
  misconfigurations: string;
}

@Component({
  selector: 'ziro-misconfigured-voice-apps',
  templateUrl: './misconfigured-voice-apps.component.html',
  styleUrls: ['../../reporting.scss'],
  providers: [MisconfiguredVoiceAppsResource],
  standalone: false,
})
export class MisconfiguredVoiceAppsComponent implements OnInit, OnDestroy {
  private _breadcrumbService = inject(BreadcrumbsService);
  private _translateService = inject(TranslateService);
  private _misconfiguredVoiceAppsResource = inject(MisconfiguredVoiceAppsResource);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _authenticationContext = inject(AuthenticationContext);
  private _datatableFilterService = inject(DatatableFilterService);
  private _subscriptions = new Subscription();

  isLoading = true;
  tableDataAutomation = 'misconfigured-voice-apps-table';

  voiceAppTypeFilterOptions: FilterSelectOption[] = [
    {
      label: this._translateService.instant(
        'tkey;reports.microsoft.misconfigured_voice_apps.table.filter.voice_application_type.auto_attendant.label',
      ),
      value: VoiceAppFilterType.AUTO_ATTENDANT,
    },
    {
      label: this._translateService.instant(
        'tkey;reports.microsoft.misconfigured_voice_apps.table.filter.voice_application_type.call_queue.label',
      ),
      value: VoiceAppFilterType.CALL_QUEUE,
    },
  ];

  datatableRows: MisconfiguredVoiceAppRow[] = [];
  datatableCols: DatatableColumn<MisconfiguredVoiceAppRow>[] = [
    {
      name: 'name',
      label: 'tkey;reports.microsoft.misconfigured_voice_apps.table.rows.name',
      columnCharacterWidth: 30,
    },
    {
      name: 'voiceAppType',
      label: 'tkey;reports.microsoft.misconfigured_voice_apps.table.rows.voice_application_type',
    },
    {
      name: 'misconfigurationType',
      label: 'tkey;reports.microsoft.misconfigured_voice_apps.table.rows.misconfiguration_type',
    },
    {
      name: 'misconfigurations',
      label: 'tkey;reports.microsoft.misconfigured_voice_apps.table.rows.misconfigurations',
      columnCharacterWidth: 40,
    },
  ];

  voiceAppTypeFilterMatch = (filter: string[], row: MisconfiguredVoiceAppRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.voiceAppType);

  private _tenantId: string;
  private _userHasAutoAttendantPrivilege = false;
  private _userHasCallQueuePrivilege = false;

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.paramMap.get('tenantId');

    this._breadcrumbService.updateBreadcrumbs([{ label: 'tkey;reports.microsoft.misconfigured_voice_apps.title' }]);

    const reportObs = this._misconfiguredVoiceAppsResource.get(this._tenantId);

    const sub = combineLatest([reportObs, this._authenticationContext.state$]).subscribe(([report, currentUser]) => {
      this._userHasAutoAttendantPrivilege = this._authenticationContext.userHasPrivilege(
        currentUser,
        ZpmPrivilege.AUTO_ATTENDANT_MANAGEMENT,
        this._tenantId,
      );
      this._userHasCallQueuePrivilege = this._authenticationContext.userHasPrivilege(
        currentUser,
        ZpmPrivilege.CALL_QUEUE_MANAGEMENT,
        this._tenantId,
      );
      this._initDatatable(report);
      this.isLoading = false;
    });

    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _initDatatable(report: MisconfiguredVoiceAppReport): void {
    const rows: MisconfiguredVoiceAppRow[] = [];

    this._pushRows(rows, report?.misconfiguredAutoAttendants, VoiceAppFilterType.AUTO_ATTENDANT);
    this._pushRows(rows, report?.misconfiguredCallQueues, VoiceAppFilterType.CALL_QUEUE);

    rows.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    this.datatableRows = rows;
  }

  private _pushRows(
    rows: MisconfiguredVoiceAppRow[],
    items: MisconfiguredVoiceApp[],
    voiceAppType: VoiceAppFilterType,
  ): void {
    const isAA = voiceAppType === VoiceAppFilterType.AUTO_ATTENDANT;
    const hasPrivilege = isAA ? this._userHasAutoAttendantPrivilege : this._userHasCallQueuePrivilege;

    (items || []).forEach((item) => {
      const actions: DatatableRowActionButton<MisconfiguredVoiceAppRow>[] = [];

      if (hasPrivilege) {
        actions.push({
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_PRIMARY,
          icon: ZiroIcons.NEXT,
          dataAutomation: `edit-${isAA ? 'auto-attendant' : 'call-queue'}`,
          onClick: () => this._navigateToEditView(item.guid, isAA),
        });
      }

      rows.push({
        guid: item.guid,
        name: item.name,
        voiceAppType: voiceAppType,
        misconfigurationType: this._readableMisconfigurationType(item.misconfigurationType),
        misconfigurations: this._formatMisconfigurations(item),
        actions,
      });
    });
  }

  private _navigateToEditView(guid: string, isAA: boolean): void {
    const returnUrl = this._router.url;
    if (isAA) {
      this._router.navigate(['auto-attendant-management', 'tenants', this._tenantId, 'teams-auto-attendants', guid], {
        queryParams: { returnUrl },
      });
    } else {
      this._router.navigate(['call-queue-management', 'tenants', this._tenantId, 'teams-call-queues', guid], {
        queryParams: { returnUrl },
      });
    }
  }

  private _readableMisconfigurationType(type: MisconfigurationType): string {
    switch (type) {
      case MisconfigurationType.EMPTY_CALL_QUEUE: {
        return this._translateService.instant(
          'tkey;reports.microsoft.misconfigured_voice_apps.table.misconfiguration_type.empty_call_queue',
        );
      }
      case MisconfigurationType.CONTAINS_INVALID_ENTITIES: {
        return this._translateService.instant(
          'tkey;reports.microsoft.misconfigured_voice_apps.table.misconfiguration_type.invalid_references',
        );
      }
      default: {
        return type;
      }
    }
  }

  private _formatMisconfigurations(item: MisconfiguredVoiceApp): string {
    if (!item) {
      return '';
    }

    if (item.misconfigurationType === MisconfigurationType.EMPTY_CALL_QUEUE) {
      return this._translateService.instant(
        'tkey;reports.microsoft.misconfigured_voice_apps.table.empty_call_queue.label',
      );
    }

    if (item.misconfigurationType === MisconfigurationType.CONTAINS_INVALID_ENTITIES) {
      return this._formatInvalidEntitiesSummary(item.invalidEntities || []);
    }

    return '';
  }

  private _formatInvalidEntitiesSummary(entities: InvalidVoiceAppEntity[]): string {
    const userCount = entities.filter((e) => e.entityType === EntityType.USER).length;
    const groupCount = entities.filter((e) => e.entityType === EntityType.GROUP).length;
    const voiceAppCount = entities.filter((e) => e.entityType === EntityType.VOICE_APP).length;
    const resourceAccountCount = entities.filter((e) => e.entityType === EntityType.RESOURCE_ACCOUNT).length;

    const parts: string[] = [];
    if (userCount > 0) {
      parts.push(
        this._translateService.instant(
          userCount === 1
            ? 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.user.singular'
            : 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.user.plural',
          { count: userCount },
        ),
      );
    }
    if (groupCount > 0) {
      parts.push(
        this._translateService.instant(
          groupCount === 1
            ? 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.group.singular'
            : 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.group.plural',
          { count: groupCount },
        ),
      );
    }
    if (voiceAppCount > 0) {
      parts.push(
        this._translateService.instant(
          voiceAppCount === 1
            ? 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.voice_app.singular'
            : 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.voice_app.plural',
          { count: voiceAppCount },
        ),
      );
    }
    if (resourceAccountCount > 0) {
      parts.push(
        this._translateService.instant(
          resourceAccountCount === 1
            ? 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.resource_account.singular'
            : 'tkey;reports.microsoft.misconfigured_voice_apps.table.invalid_entities.resource_account.plural',
          { count: resourceAccountCount },
        ),
      );
    }

    return parts.join(', ');
  }
}
